import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";

import DarkModeSelect from "../../components/DarkModeSelect";

const ProjectListPage = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAssignPopup, setShowAssignPopup] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState({});
    const [formattedUsers, setFormattedUsers] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState({});
    const [formattedProjects, setFormattedProjects] = useState([]);

    const navigate = useNavigate();

    const fetchStatus = async (openstackIds) => {
            try {
                const statusData = await axiosInstance.post(
                    "/project/openstack/",
                    { "openstack_ids": openstackIds }
                );
                setProjects((prev) =>
                    prev.map((p) => {
                        const statusItem = statusData.data.find(
                            (item) => p.openstack_id in item
                        );
                        return {
                            ...p,
                            is_enabled: statusItem
                                ? statusItem[p.openstack_id]
                                : p.is_enabled || null,
                        };
                    })
                );
            } catch (err) {
                console.error("❌ Failed to fetch status", err);
            }
        };

    const fetchProjects = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get("/project/projects/");
            const data = (res.data || []).map((p) => ({
                ...p,
                is_enabled: null,
            }));
            setProjects(data);

            const projectOptions = data.map((project) => ({
                value: project.project_id,
                label: `${project.openstack_id} - ` + project.project_name,
            }));
            setFormattedProjects(projectOptions);

            const openstackIds = data.map((p) => p.openstack_id);
            if (openstackIds.length > 0) {
                fetchStatus(openstackIds);
            }
        } catch (err) {
            console.error("❌ Failed to fetch projects:", err);
        } finally {
            setLoading(false);
        }
    }, [setProjects, setFormattedProjects]);

    const fetchUsers = useCallback(async () => {
        try {
            const res = await axiosInstance.get("/auth/users/", {
                params: {
                    is_active: true,
                },
            });
            const userOptions = res.data.map((user) => ({
                value: user.user_id,
                label: user.username,
            }));
            setFormattedUsers(userOptions);
        } catch (err) {
            console.error("❌ Failed to fetch users:", err);
        }
    }, [setFormattedUsers]);

    useEffect(() => {
        fetchProjects();
        fetchUsers();
    }, [fetchProjects, fetchUsers]);

    const handleAssign = async () => {
        try {
            await axiosInstance.post("/project/assign-user-to-project/", {
                project_id: selectedProjectId.value,
                user_id: selectedUserId.value,
            });
            alert("✅ User assigned successfully!");
            setShowAssignPopup(false);
            fetchProjects();
        } catch (err) {
            console.error("❌ Assignment failed:", err);
            alert("❌ Failed to assign user.");
        }
    };

    const handleRowClick = (projectId) => {
        navigate(`/admin/project-detail/${projectId}`);
    };

    const getStatusProps = (isEnabled) => {
        if (isEnabled === true) {
            return {
                className: "bg-green-600/20 text-green-400",
                displayText: "Enabled",
            };
        } else if (isEnabled === false) {
            return {
                className: "bg-red-600/20 text-red-400",
                displayText: "Disabled",
            };
        } else {
            return {
                className: "animate-pulse bg-gray-700 rounded w-full",
                displayText: "",
            };
        }
    };

    return (
        <div className="min-h-screen text-white">
            <main className="container mx-auto px-4 py-6">
                <section className="mx-auto py-5">
                    <header className="container mx-auto mb-4 flex flex-col md:flex-row items-center justify-between gap-4">
                        <h2 className="text-4xl font-extrabold text-green-400 tracking-tight drop-shadow-lg">
                            🏗️ Project Overview
                        </h2>
                        <div className="flex gap-4">
                            <Link
                                to="/admin/create-project"
                                className="inline-block px-6 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-purple-500 hover:to-indigo-600 text-white font-semibold shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400"
                            >
                                ➕ Create New Project
                            </Link>
                            <button
                                onClick={() => setShowAssignPopup(true)}
                                className="inline-block px-6 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-500 hover:from-emerald-500 hover:to-green-600 text-white font-semibold shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400"
                            >
                                🔗 Assign User to Project
                            </button>
                        </div>
                    </header>

                    <div className="container mx-aut0 pb-6">
                        <div className="bg-black/40 backdrop-blur-lg border border-gray-700 rounded-2xl shadow-2xl overflow-x-auto">
                            <table className="min-w-full table-auto text-sm">
                                <thead className="bg-gradient-to-r from-gray-800 to-gray-700 text-gray-300 text-xs uppercase tracking-wider">
                                    <tr>
                                        {[
                                            "Name",
                                            "OpenStack ID",
                                            "Package",
                                            "User",
                                            "Status",
                                        ].map((title) => (
                                            <th
                                                key={title}
                                                className="px-6 py-4 text-left font-semibold"
                                            >
                                                {title}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800">
                                    {loading ? (
                                        <tr>
                                            <td
                                                colSpan="5"
                                                className="text-center py-10 text-gray-500 font-medium"
                                            >
                                                ⏳ Loading projects...
                                            </td>
                                        </tr>
                                    ) : projects.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan="5"
                                                className="text-center py-10 text-gray-500 font-medium"
                                            >
                                                🚫 No projects found.
                                            </td>
                                        </tr>
                                    ) : (
                                        projects.map((proj) => {
                                            const { className, displayText } =
                                                getStatusProps(proj.is_enabled);
                                            return (
                                                <tr
                                                    key={proj.project_id}
                                                    onClick={() =>
                                                        handleRowClick(
                                                            proj.openstack_id
                                                        )
                                                    }
                                                    className="hover:bg-white/5 hover:backdrop-blur-sm transition duration-200 cursor-pointer"
                                                >
                                                    <td className="px-6 py-4 font-medium text-indigo-300">
                                                        {proj.project_name}
                                                    </td>
                                                    <td className="px-6 py-4 text-xs text-gray-400 break-all">
                                                        {proj.openstack_id}
                                                    </td>
                                                    <td className="px-6 py-4 text-gray-300">
                                                        {proj.project_type
                                                            ?.name ?? "—"}
                                                    </td>
                                                    <td className="px-6 py-4 text-gray-300">
                                                        {proj.username ?? "—"}
                                                    </td>
                                                    <td className="px-6 py-4 w-40">
                                                        <span
                                                            className={`inline-block px-3 py-1 text-xs font-bold rounded-full ${className}`}
                                                        >
                                                            {displayText}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Popup for assigning user */}
                    {showAssignPopup && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-lg text-white border border-gray-700 shadow-2xl">
                                <h2 className="text-xl font-semibold mb-4 text-indigo-400">
                                    Assign User to Project
                                </h2>

                                <div className="mb-4">
                                    <label className="block text-sm mb-1">
                                        Search Project by OpenStack ID
                                    </label>
                                    <DarkModeSelect
                                        options={formattedProjects}
                                        value={selectedProjectId}
                                        onChange={setSelectedProjectId}
                                    />
                                </div>

                                <div className="mb-6">
                                    <label className="block text-sm mb-1">
                                        Search User
                                    </label>
                                    <DarkModeSelect
                                        options={formattedUsers}
                                        value={selectedUserId}
                                        onChange={setSelectedUserId}
                                    />
                                </div>

                                <div className="flex justify-end gap-4">
                                    <button
                                        onClick={() =>
                                            setShowAssignPopup(false)
                                        }
                                        className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleAssign}
                                        className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white font-semibold"
                                        disabled={
                                            !selectedUserId ||
                                            !selectedProjectId
                                        }
                                    >
                                        Assign
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default ProjectListPage;
